import React, { useState, useRef, useEffect } from 'react';
import { X, Search, MapPin, Calendar, ChevronLeft, ChevronRight, Loader2, UserCheck, CreditCard } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import AddCardModal from './AddCardModal';

function InlineCardForm({ clientId, apiService, onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    try {
      const { client_secret } = await apiService.createSetupIntent(clientId);
      const { error, setupIntent } = await stripe.confirmCardSetup(client_secret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (error) { onError(error.message); return; }
      if (setupIntent.status === 'succeeded') {
        const result = await apiService.confirmCardSaved(clientId, setupIntent.id);
        onSuccess(result);
      }
    } catch (err) {
      onError(err.message || 'Failed to save card');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="border border-gray-200 rounded-lg p-3 mb-3 bg-white">
        <CardElement options={{
          style: {
            base: { fontSize: '15px', color: '#1a1a1a', '::placeholder': { color: '#9ca3af' } },
            invalid: { color: '#dc2626' },
          },
        }} />
      </div>
      <button type="submit" disabled={!stripe || processing}
        className="w-full py-2.5 bg-green-700 text-white text-sm font-medium rounded-lg hover:bg-green-800 disabled:opacity-50 flex items-center justify-center gap-2">
        <CreditCard className="w-4 h-4" />
        {processing ? 'Saving card...' : 'Save Card & Review Booking'}
      </button>
    </form>
  );
}

function BookingAssistant({ isOpen, onClose, apiService, navigate, embedded = false, prefillDate = null, prefillTime = null }) {
  const [step, setStep] = useState('start'); // start, zip, technicians, calendar, client, services, card, confirm, success
  const [bookingPath, setBookingPath] = useState(null); // 'existing' or 'new'
  const [isForSelf, setIsForSelf] = useState(null);
  const [clientAge, setClientAge] = useState('');
  const [medicalNotes, setMedicalNotes] = useState('');
  const [contactPerson, setContactPerson] = useState({ name: '', phone: '', email: '', relationship: '' });
  const [cardInfo, setCardInfo] = useState({ number: '', exp: '', cvv: '', zip: '' });
  const [cardCollected, setCardCollected] = useState(false);
  const [personMatches, setPersonMatches] = useState([]);
  const personMatchTimeout = useRef(null);
  const [creatingClient, setCreatingClient] = useState(false);
  const [bookedAppointmentId, setBookedAppointmentId] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [cardError, setCardError] = useState(null);
  const [zip, setZip] = useState('');
  const [searching, setSearching] = useState(false);
  const [zipResult, setZipResult] = useState(null);
  const [error, setError] = useState(null);
  const [selectedTech, setSelectedTech] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [daysOff, setDaysOff] = useState([]);
  const [loadingCal, setLoadingCal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(prefillDate || null);
  const [selectedTime, setSelectedTime] = useState(prefillTime || '10:00');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const [allServices, setAllServices] = useState([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [serviceQuantities, setServiceQuantities] = useState({}); // { serviceId: qty }
  const [serviceSearch, setServiceSearch] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [note, setNote] = useState('');
  const [overlapCheck, setOverlapCheck] = useState(null);
  const [checkingOverlap, setCheckingOverlap] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cardLinkSending, setCardLinkSending] = useState(false);
  const [cardLinkSent, setCardLinkSent] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const inputRef = useRef(null);
  const clientSearchTimeout = useRef(null);

  const resetState = () => {
    setStep('start');
    setBookingPath(null);
    setIsForSelf(null);
    setClientAge('');
    setMedicalNotes('');
    setContactPerson({ name: '', phone: '', email: '', relationship: '' });
    setCardInfo({ number: '', exp: '', cvv: '', zip: '' });
    setCardCollected(false);
    setPersonMatches([]);
    setBookedAppointmentId(null);
    setZip('');
    setSearching(false);
    setZipResult(null);
    setError(null);
    setSelectedTech(null);
    setWeekOffset(0);
    setAppointments([]);
    setDaysOff([]);
    setSelectedDate(null);
    setSelectedTime('10:00');
    setSelectedClient(null);
    setClientSearch('');
    setClientResults([]);
    setSearchingClients(false);
    setShowNewClient(false);
    setNewClient({ first_name: '', last_name: '', phone: '', email: '' });
    setAllServices([]);
    setSelectedServiceIds([]);
    setAddress('');
    setCity('');
    setZipCode('');
    setNote('');
    setOverlapCheck(null);
    setCheckingOverlap(false);
    setSubmitting(false);
    setCardLinkSending(false);
    setCardLinkSent(false);
  };

  const handleClose = () => {
    onClose();
    setTimeout(resetState, 300);
  };

  const searchZip = async () => {
    if (!zip.trim() || zip.length < 5) return;
    setSearching(true);
    setError(null);
    try {
      const data = await apiService.zipLookup(zip.trim());
      setZipResult(data);
      setStep('technicians');
    } catch (err) {
      setError('Zip code not found. Please try another.');
    } finally {
      setSearching(false);
    }
  };

  const selectTechnician = async (tech) => {
    setSelectedTech(tech);
    setStep('services');
    loadServices();
  };

  const loadCalendarData = async (techId, offset) => {
    setLoadingCal(true);
    try {
      const today = new Date();
      const monday = new Date(today);
      monday.setDate(today.getDate() - today.getDay() + 1 + (offset * 7));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

      const [aptsData, offData] = await Promise.all([
        apiService.getAppointments({
          technician: techId,
          date_from: fmt(monday),
          date_to: fmt(sunday),
          status__in: 'ACCEPTED,COMPLETED',
          page_size: 100,
        }),
        apiService.getTechnicianDaysOff({
          technician: techId,
          date_from: fmt(monday),
          date_to: fmt(sunday),
        }),
      ]);

      setAppointments(aptsData.results || aptsData || []);
      setDaysOff(offData.results || offData || []);
    } catch (err) {
      console.error('Failed to load calendar data:', err);
    } finally {
      setLoadingCal(false);
    }
  };

  const changeWeek = (delta) => {
    const newOffset = weekOffset + delta;
    setWeekOffset(newOffset);
    if (selectedTech) loadCalendarData(selectedTech.id, newOffset);
  };

  const getWeekDates = () => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1 + (weekOffset * 7));
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
  };

  const formatTime = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
  };

  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const checkOverlap = async (techId, dateStr, timeStr) => {
    setCheckingOverlap(true);
    setOverlapCheck(null);
    try {
      const dt = `${dateStr}T${timeStr}:00`;
      const totalDuration = allServices.filter(s => selectedServiceIds.includes(s.id)).reduce((sum, s) => sum + (s.duration_minutes || 60), 0) || 60;
      const data = await apiService.get('/amn/appointments/check-overlap/', { technician_id: techId, date_time: dt, duration: totalDuration, zip_code: zipCode || zip });
      setOverlapCheck(data);
    } catch { setOverlapCheck(null); }
    finally { setCheckingOverlap(false); }
  };

  const loadServices = async () => {
    try {
      const data = await apiService.get('/amn/services/', { active: true, ordering: '-booking_count', page_size: 100 });
      setAllServices(data.results || data);
    } catch {}
  };

  const searchPersonMatches = (firstName, lastName) => {
    if (personMatchTimeout.current) clearTimeout(personMatchTimeout.current);
    if (!firstName || firstName.length < 2) { setPersonMatches([]); return; }
    personMatchTimeout.current = setTimeout(async () => {
      try {
        const params = { search: `${firstName} ${lastName || ''}`.trim(), page_size: 5 };
        const data = await apiService.get('/amn/persons/', params);
        const results = (data.results || data || []).filter(p =>
          p.first_name && p.first_name.toLowerCase().startsWith(firstName.toLowerCase())
        );
        setPersonMatches(results);
      } catch { setPersonMatches([]); }
    }, 400);
  };

  const searchClients = async (term) => {
    if (term.length < 2) { setClientResults([]); return; }
    setSearchingClients(true);
    try {
      const data = await apiService.get('/amn/clients/', { search: term, page_size: 10, ordering: '-appointment_count' });
      setClientResults(data.results || data);
    } catch {}
    finally { setSearchingClients(false); }
  };

  const handleSendCardLink = async () => {
    if (!selectedClient) return;
    setCardLinkSending(true);
    try {
      await apiService.post('/amn/payments/send-card-link/', { client_id: selectedClient.id, method: 'sms' });
      setCardLinkSent(true);
    } catch (err) { setError(err.message || 'Failed to send card link'); }
    finally { setCardLinkSending(false); }
  };

  const handleBook = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        technician_id: selectedTech.id,
        service_ids: selectedServiceIds,
        service_quantities: serviceQuantities,
        date_time: `${selectedDate}T${selectedTime}:00`,
        address, city, state: 'FL', zip_code: zipCode,
        additional_note: note,
      };
      if (selectedClient) {
        payload.client_id = selectedClient.id;
      } else if (showNewClient) {
        payload.new_client = newClient;
      }
      if (clientAge) payload.client_age = clientAge;
      if (medicalNotes) payload.medical_notes = medicalNotes;
      if (isForSelf === false && contactPerson.name) payload.contact_person = contactPerson;
      if (cardCollected && cardInfo.number) payload.card_info = cardInfo;
      const result = await apiService.createAppointmentFull(payload);
      if (result.appointment?.id) setBookedAppointmentId(result.appointment.id);

      // Update selectedClient to the newly created client for success screen links
      const newClientId = result.appointment?.client?.id || result.appointment?.client;
      if (newClientId && !selectedClient) {
        try {
          const clientData = await apiService.get(`/amn/clients/${newClientId}/`);
          setSelectedClient(clientData);
        } catch {}
      }

      setStep('success');
    } catch (err) { setError(err.message || 'Failed to create appointment'); }
    finally { setSubmitting(false); }
  };

  if (!isOpen && !embedded) return null;

  const weekDates = getWeekDates();
  const todayStr = formatDate(new Date());

  return (
    <div className={embedded ? "flex-1 bg-neutral-50 overflow-y-auto" : "fixed inset-0 z-50 flex flex-col bg-neutral-50"} style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className={embedded ? "max-w-2xl mx-auto w-full p-3" : "flex-1 flex justify-center overflow-y-auto"}>
      <div className={embedded ? "" : "bg-white w-full max-w-2xl flex flex-col min-h-full shadow-sm"}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-white rounded-t-lg">
          <div className="flex items-center gap-2">
            {step !== 'start' && step !== 'success' && (
              <button
                onClick={() => {
                  if (step === 'zip') { setStep('start'); setBookingPath(null); }
                  else if (step === 'technicians') { setStep('zip'); setZipResult(null); }
                  else if (step === 'services') { setStep('technicians'); setSelectedTech(null); }
                  else if (step === 'calendar') { setStep('services'); setSelectedDate(null); }
                  else if (step === 'client') { setStep('calendar'); }
                  else if (step === 'card') { setStep('client'); }
                  else if (step === 'confirm') { setStep('card'); }
                  else { setStep('start'); }
                }}
                className="p-1 rounded hover:bg-gray-100"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
            )}
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {step === 'start' && 'New Booking'}
                {step === 'zip' && (bookingPath === 'existing' ? 'Find Client' : 'Service Location')}
                {step === 'technicians' && `Team Members near ${zip}`}
                {step === 'services' && `Services for ${selectedTech?.name}`}
                {step === 'calendar' && `${selectedTech?.name}'s Availability`}
                {step === 'client' && (bookingPath === 'new' ? 'Client Information' : 'Client & Details')}
                {step === 'card' && 'Payment Method'}
                {step === 'confirm' && 'Confirm Booking'}
                {step === 'success' && 'Booking Confirmed'}
              </p>
              {zipResult && step !== 'zip' && (
                <p className="text-xs text-gray-500">{zipResult.city}{zipResult.county ? `, ${zipResult.county}` : ''}</p>
              )}
            </div>
          </div>
          {!embedded && (
            <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-gray-100">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          )}
          {embedded && step !== 'start' && step !== 'success' && (
            <button onClick={resetState} className="text-xs text-gray-500 hover:text-gray-700 font-medium">
              Start Over
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5" style={{ minHeight: 450 }}>
          {/* Step 0: Choose Path */}
          {step === 'start' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">How would you like to start?</p>

              <button
                onClick={() => { setBookingPath('existing'); setStep('zip'); }}
                className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-red-300 hover:bg-red-50/30 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                    <UserCheck className="w-5 h-5 text-red-800" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Existing Client</p>
                    <p className="text-xs text-gray-500">Search by name or phone — their info is already on file</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => { setBookingPath('new'); setStep('zip'); }}
                className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-red-300 hover:bg-red-50/30 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <MapPin className="w-5 h-5 text-blue-700" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">New Client</p>
                    <p className="text-xs text-gray-500">Start with zip code — follow the booking flow</p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Step 1: Zip Code or Client Search */}
          {step === 'zip' && (
            <div>
              {/* EXISTING CLIENT PATH */}
              {bookingPath === 'existing' && (
              <div className="mb-3">
                <p className="text-sm text-gray-600 mb-3">Search for the client by name or phone number.</p>
                {selectedClient ? (
                  <div className="bg-gray-50 rounded-lg p-3 mb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{selectedClient.person_name || `${selectedClient.person?.first_name || ''} ${selectedClient.person?.last_name || ''}`.trim()}</p>
                        {(selectedClient.phone_number || selectedClient.person?.phone_number) && <p className="text-xs text-gray-500">{selectedClient.phone_number || selectedClient.person.phone_number}</p>}
                      </div>
                      <button type="button" onClick={() => { setSelectedClient(null); setZip(''); }} className="text-xs text-red-600 hover:text-red-800">Change</button>
                    </div>
                    {selectedClient.has_card_on_file
                      ? <p className="text-xs text-green-600 mt-1">💳 {selectedClient.card_brand} ****{selectedClient.card_last_four}</p>
                      : <p className="text-xs text-amber-600 mt-1">⚠️ No card on file</p>}
                    {selectedClient.notes && (
                      <div className="mt-2 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
                        <p className="text-[10px] font-medium text-amber-700 mb-0.5">📋 Client Notes</p>
                        <p className="text-xs text-amber-900 leading-relaxed">{selectedClient.notes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={clientSearch}
                    onChange={e => { setClientSearch(e.target.value); if (clientSearchTimeout.current) clearTimeout(clientSearchTimeout.current); clientSearchTimeout.current = setTimeout(() => searchClients(e.target.value), 300); }}
                    placeholder="Search by name or phone..."
                    className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    autoFocus
                  />
                  {clientResults.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto">
                      {clientResults.map(c => {
                        const clientZip = c.effective_zip_code || c.override_zip_code || c.person?.zip_code || '';
                        return (
                          <button key={c.id} type="button" onClick={async () => {
                            setSelectedClient(c);
                            setClientSearch('');
                            setClientResults([]);
                            setAddress(c.effective_address || c.override_address || c.person?.address || '');
                            setCity(c.effective_city || c.override_city || c.person?.city || '');
                            setZipCode(c.effective_zip_code || c.override_zip_code || c.person?.zip_code || '');
                            if (c.last_booking_note) setNote(c.last_booking_note);
                            if (clientZip && clientZip.length >= 5) {
                              setZip(clientZip);
                              // Auto-search zip to advance to technician selection
                              try {
                                setSearching(true);
                                const data = await apiService.zipLookup(clientZip);
                                setZipResult(data);
                                setStep('technicians');
                              } catch (err) {
                                setError('Zip code not found. Please try another.');
                              } finally {
                                setSearching(false);
                              }
                            }
                          }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0">
                            <span className="font-medium">{c.person_name || `${c.person?.first_name || ''} ${c.person?.last_name || ''}`.trim() || 'Unknown'}</span>
                            {(c.phone_number || c.person?.phone_number) && <span className="text-gray-400 ml-2 text-xs">{c.phone_number || c.person.phone_number}</span>}
                            {clientZip ? <span className="text-gray-400 text-xs ml-2">ZIP {clientZip}</span> : <span className="text-red-400 text-xs ml-2">no zip</span>}
                            {c.has_card_on_file ? <span className="text-green-600 text-xs ml-2">💳 card</span> : <span className="text-amber-600 text-xs ml-2">no card</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                )}

                {/* If existing client selected but no zip, show zip entry */}
                {selectedClient && !zipResult && (
                  <>
                    <div className="flex items-center gap-3 my-3">
                      <div className="flex-1 border-t border-gray-200" />
                      <span className="text-xs text-gray-400">appointment location</span>
                      <div className="flex-1 border-t border-gray-200" />
                    </div>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input ref={inputRef} type="text" value={zip}
                          onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                          onKeyDown={(e) => e.key === 'Enter' && searchZip()}
                          placeholder="Enter zip code" maxLength={5}
                          className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent" />
                      </div>
                      <button onClick={searchZip} disabled={zip.length < 5 || searching}
                        className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${zip.length < 5 || searching ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-red-800 text-white hover:bg-red-900'}`}>
                        {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        Search
                      </button>
                    </div>
                  </>
                )}
                {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
              </div>
              )}

              {/* NEW CLIENT PATH */}
              {bookingPath === 'new' && (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  <em style={{color:'#8B2500'}}>"May I have the zip code for where you are?"</em>
                </p>

                {/* Zip code entry */}
                <div className="flex gap-2 mb-4">
                  <div className="relative flex-1">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input ref={inputRef} type="text" value={zip}
                      onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                      onKeyDown={(e) => e.key === 'Enter' && searchZip()}
                      placeholder="Enter zip code" maxLength={5}
                      className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      autoFocus />
                  </div>
                  <button onClick={searchZip} disabled={zip.length < 5 || searching}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${zip.length < 5 || searching ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-red-800 text-white hover:bg-red-900'}`}>
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    Search
                  </button>
                </div>

                {/* Who is it for? */}
                {zipResult && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      <em style={{color:'#8B2500'}}>"Would this be for yourself or a family member?"</em>
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => { setIsForSelf(true); setStep('technicians'); }}
                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${isForSelf === true ? 'border-red-800 bg-red-50 text-red-900' : 'border-gray-200 hover:border-red-300 text-gray-700'}`}>
                        For themselves
                      </button>
                      <button onClick={() => { setIsForSelf(false); setStep('technicians'); }}
                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${isForSelf === false ? 'border-red-800 bg-red-50 text-red-900' : 'border-gray-200 hover:border-red-300 text-gray-700'}`}>
                        For a family member
                      </button>
                    </div>
                  </div>
                )}
                {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
              </div>
              )}
            </div>
          )}

          {/* Step 2: Technician List */}
          {step === 'technicians' && zipResult && (
            <div>
              {zipResult.technicians.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-500">No team members found near this area.</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {zipResult.technicians.map((tech) => {
                    const beyondMax = tech.beyond_max;
                    const outOfRange = beyondMax && tech.distance_miles > (tech.max_travel_distance || 45) + 30;
                    const confirmFirst = beyondMax && !outOfRange;
                    return (
                    <button
                      key={tech.id}
                      onClick={() => selectTechnician(tech)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors text-left ${
                        outOfRange
                          ? 'border-red-200 bg-red-50/50 hover:border-red-300 opacity-70'
                          : confirmFirst
                          ? 'border-amber-300 bg-amber-50 hover:border-amber-400'
                          : 'border-gray-200 hover:border-red-300 hover:bg-red-50'
                      }`}
                    >
                      <div>
                        <p className={`text-sm font-medium ${outOfRange ? 'text-gray-500' : 'text-gray-900'}`}>{tech.name}</p>
                        <p className="text-xs text-gray-500">
                          {tech.location || 'No location'} · {tech.distance_miles} mi
                          {tech.max_travel_distance && <span className="text-gray-400"> (max {tech.max_travel_distance} mi)</span>}
                        </p>
                        {confirmFirst && (
                          <p className="text-[10px] text-amber-700 font-medium mt-0.5">
                            ⚠️ Beyond max distance — confirm with tech first
                          </p>
                        )}
                        {outOfRange && (
                          <p className="text-[10px] text-red-600 font-medium mt-0.5">
                            🚫 Out of range — rare cases only
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className={`text-xs font-medium ${tech.open_slots > 10 ? 'text-green-600' : tech.open_slots > 3 ? 'text-amber-600' : 'text-red-500'}`}>
                          {tech.open_slots > 10 ? 'Good availability' : tech.open_slots > 3 ? 'Limited availability' : tech.open_slots > 0 ? 'Very limited' : 'Nearly full'}
                        </p>
                        <p className="text-[10px] text-gray-400">{tech.appts_next_2w} appts next 2 wks</p>
                      </div>
                    </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Calendar View */}
          {step === 'calendar' && selectedTech && (
            <div>
              {/* Week Navigation */}
              <div className="flex items-center justify-between mb-3">
                <button onClick={() => changeWeek(-1)} className="p-1 rounded hover:bg-gray-100">
                  <ChevronLeft className="w-4 h-4 text-gray-600" />
                </button>
                <div className="text-center">
                  <p className="text-xs font-medium text-gray-900">
                    {weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                  {weekOffset !== 0 && (
                    <button onClick={() => { setWeekOffset(0); loadCalendarData(selectedTech.id, 0); }} className="text-[10px] text-blue-600 hover:text-blue-700">
                      Back to this week
                    </button>
                  )}
                </div>
                <button onClick={() => changeWeek(1)} className="p-1 rounded hover:bg-gray-100">
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              {loadingCal ? (
                <div className="text-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
                </div>
              ) : (
                <div className="space-y-1">
                  {weekDates.map((date) => {
                    const dateStr = formatDate(date);
                    const isToday = dateStr === todayStr;
                    const isPast = dateStr < todayStr;
                    const dayAppts = appointments.filter(a => a.date_time && a.date_time.startsWith(dateStr));
                    const dayOff = daysOff.find(d => d.date === dateStr && d.block_type === 'all_day');
                    const timeBlocks = daysOff.filter(d => d.date === dateStr && d.block_type === 'time_range');

                    return (
                      <div
                        key={dateStr}
                        onClick={() => {
                          if (!dayOff && !isPast) {
                            setSelectedDate(dateStr);
                            // Find first open time slot using selected service duration + 30min travel
                            const serviceDuration = allServices.filter(s => selectedServiceIds.includes(s.id)).reduce((sum, s) => sum + (s.duration_minutes || 60), 0) || 60;
                            const blockNeeded = serviceDuration + 30;
                            const bookedMins = dayAppts.map(a => { const d = new Date(a.date_time); const h = parseInt(d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', timeZone: 'America/New_York' })); const m = parseInt(d.toLocaleTimeString('en-US', { hour12: false, minute: '2-digit', timeZone: 'America/New_York' }).split(':')[1] || '0'); return h * 60 + m; });
                            // Convert time blocks to blocked minute ranges
                            const blockedRanges = timeBlocks.map(b => ({ start: parseInt(b.start_time) * 60 + parseInt((b.start_time || '').split(':')[1] || '0'), end: parseInt(b.end_time) * 60 + parseInt((b.end_time || '').split(':')[1] || '0') }));
                            const slots = ['09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00'];
                            const openSlot = slots.find(s => {
                              const sm = parseInt(s) * 60 + parseInt(s.split(':')[1]);
                              const slotEnd = sm + blockNeeded;
                              const hitsAppt = bookedMins.some(b => Math.abs(sm - b) < blockNeeded);
                              const hitsBlock = blockedRanges.some(r => sm < r.end && slotEnd > r.start);
                              return !hitsAppt && !hitsBlock;
                            }) || '09:00';
                            setSelectedTime(openSlot);
                            checkOverlap(selectedTech.id, dateStr, openSlot);
                          }
                        }}
                        className={`rounded-lg border p-2.5 ${
                          dayOff ? 'bg-gray-200 border-gray-300' :
                          selectedDate === dateStr ? 'bg-white border-red-300 ring-2 ring-red-800' :
                          isToday ? 'bg-blue-50 border-blue-200' :
                          isPast ? 'bg-gray-50 border-gray-200 opacity-60' :
                          'bg-white border-gray-200 hover:border-red-200 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <p className={`text-xs font-semibold ${isToday ? 'text-blue-700' : 'text-gray-900'}`}>
                            {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'America/New_York' })}
                          </p>
                          {(() => {
                            if (dayOff) return <span className="text-[10px] font-medium text-gray-600 bg-gray-300 px-1.5 py-0.5 rounded">DAY OFF</span>;
                            if (isPast) return null;
                            const svcDur = allServices.filter(s => selectedServiceIds.includes(s.id)).reduce((sum, s) => sum + (s.duration_minutes || 60), 0) || 60;
                            const block = svcDur + 30;
                            const apptMins = dayAppts.map(a => { const d = new Date(a.date_time); const et = new Date(d.toLocaleString('en-US', { timeZone: 'America/New_York' })); return et.getHours() * 60 + et.getMinutes(); }).sort((a,b) => a - b);
                            // Merge time blocks into occupied minutes
                            const blockedRanges = timeBlocks.map(b => ({ start: parseInt(b.start_time) * 60 + parseInt((b.start_time || '').split(':')[1] || '0'), end: parseInt(b.end_time) * 60 + parseInt((b.end_time || '').split(':')[1] || '0') }));
                            const noon = 12 * 60;
                            const checkGaps = (start, end) => {
                              // Build sorted list of all occupied intervals (appointments as 90min blocks + time blocks)
                              const occupied = [
                                ...apptMins.filter(m => m >= start && m < end).map(m => ({ s: m, e: m + 90 })),
                                ...blockedRanges.filter(r => r.start < end && r.end > start).map(r => ({ s: Math.max(r.start, start), e: Math.min(r.end, end) })),
                              ].sort((a, b) => a.s - b.s);
                              let prev = start;
                              for (const o of occupied) {
                                if (o.s - prev >= block) return true;
                                prev = Math.max(prev, o.e);
                              }
                              return end - prev >= block;
                            };
                            const amOpen = checkGaps(9 * 60, noon);
                            const pmOpen = checkGaps(noon, 17 * 60);
                            if (amOpen && pmOpen) return <span className="text-[10px] font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded">AM + PM open</span>;
                            if (amOpen) return <span className="text-[10px] font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded">AM open</span>;
                            if (pmOpen) return <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">PM only</span>;
                            return <span className="text-[10px] font-medium text-red-500 bg-red-50 px-1.5 py-0.5 rounded">Full</span>;
                          })()}
                        </div>

                        {/* Show appointments */}
                        {dayAppts.length > 0 && !dayOff && (
                          <div className="space-y-0.5 mt-1">
                            {dayAppts.sort((a, b) => a.date_time.localeCompare(b.date_time)).map((apt) => (
                              <div key={apt.id} className="flex items-center gap-2 text-[11px]">
                                <span className="text-gray-500 w-16 flex-shrink-0">{formatTime(apt.date_time)}</span>
                                <span className="text-gray-700 truncate">{apt.client_name || 'Client'}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Show time blocks */}
                        {timeBlocks.length > 0 && !dayOff && (
                          <div className="space-y-0.5 mt-1">
                            {timeBlocks.map((block) => (
                              <div key={block.id} className="text-[11px] text-gray-500">
                                <span className="bg-gray-200 px-1 rounded">Blocked {block.start_time?.slice(0,5)} – {block.end_time?.slice(0,5)}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Time picker for selected date */}
                        {selectedDate === dateStr && !dayOff && !isPast && (
                          <div className="mt-2 flex items-center gap-2" onClick={e => e.stopPropagation()}>
                            <input type="time" value={selectedTime} onChange={e => { setSelectedTime(e.target.value); checkOverlap(selectedTech.id, dateStr, e.target.value); }}
                              className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-red-800" />
                            {checkingOverlap ? <span className="text-xs text-gray-400">Checking...</span> :
                             overlapCheck?.available === false ? <span className="text-xs text-red-600">Conflict: {overlapCheck.conflict.client_name} at {new Date(overlapCheck.conflict.date_time).toLocaleTimeString('en-US', {hour:'numeric', minute:'2-digit', timeZone:'America/New_York'})}</span> :
                             overlapCheck?.available === true ? <span className="text-xs text-green-600">Available</span> : null}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Action */}
              <div className="mt-3">
                <button
                  onClick={() => {
                    if (selectedDate) {
                      if (bookingPath === 'new') setShowNewClient(true);
                      setStep('client');
                    }
                  }}
                  disabled={!selectedDate || overlapCheck?.available === false}
                  className="w-full py-2.5 bg-red-800 text-white text-sm font-medium rounded-lg hover:bg-red-900 disabled:opacity-50 transition-colors"
                >
                  {selectedDate
                    ? `Continue with ${new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at ${selectedTime}`
                    : 'Select a date to continue'}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Client Selection */}
          {step === 'client' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-700">
                  {bookingPath === 'new' ? (isForSelf === false ? 'Person Receiving Service' : 'Client Information') : 'Client'}
                </label>
                {!selectedClient && bookingPath !== 'new' && (
                  <button type="button" onClick={() => { setShowNewClient(!showNewClient); setClientSearch(''); setClientResults([]); }}
                    className="text-xs font-medium text-red-700 hover:text-red-900">
                    {showNewClient ? 'Search existing' : '+ New client'}
                  </button>
                )}
              </div>

              {selectedClient ? (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedClient.person_name || `${selectedClient.person?.first_name || ''} ${selectedClient.person?.last_name || ''}`.trim() || 'Unknown'}</p>
                      {(selectedClient.phone_number || selectedClient.person?.phone_number) && <p className="text-xs text-gray-500">{selectedClient.phone_number || selectedClient.person.phone_number}</p>}
                      {selectedClient.appointment_count > 0 && <p className="text-xs text-gray-400">{selectedClient.appointment_count} appointments · ${selectedClient.total_spent || 0} spent</p>}
                    </div>
                    <button type="button" onClick={() => { setSelectedClient(null); setCardLinkSent(false); }} className="text-xs text-red-600">Change</button>
                  </div>
                  {/* Card status */}
                  <div className="mt-2 flex items-center gap-2">
                    {selectedClient.has_card_on_file ? (
                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                        Card on file: {selectedClient.card_brand} ****{selectedClient.card_last_four}
                      </span>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">No card on file</span>
                        <button onClick={() => setShowAddCard(true)} type="button"
                          className="text-xs px-2 py-1 bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 flex items-center gap-1">
                          <CreditCard className="w-3 h-3" /> Add card now
                        </button>
                        <button onClick={handleSendCardLink} disabled={cardLinkSending} type="button"
                          className="text-xs px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50">
                          {cardLinkSending ? 'Sending...' : cardLinkSent ? 'Link sent ✓' : 'Send link via SMS'}
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Client notes */}
                  {selectedClient.notes && (
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
                      <p className="text-[10px] font-medium text-amber-700 mb-0.5">📋 Client Notes</p>
                      <p className="text-xs text-amber-900 leading-relaxed">{selectedClient.notes}</p>
                    </div>
                  )}
                </div>
              ) : showNewClient ? (
                <div className="space-y-3">
                  {/* Who is this for — first question */}
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setIsForSelf(true)}
                      className={`p-2.5 rounded-lg border-2 text-sm font-medium transition-colors ${
                        isForSelf === true ? 'border-red-800 bg-red-50 text-red-900' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}>
                      Booking for themselves
                    </button>
                    <button type="button" onClick={() => setIsForSelf(false)}
                      className={`p-2.5 rounded-lg border-2 text-sm font-medium transition-colors ${
                        isForSelf === false ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}>
                      Booking for someone else
                    </button>
                  </div>

                  {/* Contact person FIRST when booking for someone else */}
                  {isForSelf === false && (
                    <div className="bg-blue-50 rounded-lg p-3 space-y-2">
                      <p className="text-xs font-semibold text-blue-700">Main Point of Contact</p>
                      <div className="grid grid-cols-2 gap-2">
                        <input type="text" value={contactPerson.name} onChange={e => setContactPerson({...contactPerson, name: e.target.value})}
                          placeholder="Contact name *" className="px-3 py-2 text-sm border border-blue-200 rounded-md focus:ring-1 focus:ring-blue-500" autoFocus />
                        <select value={contactPerson.relationship} onChange={e => setContactPerson({...contactPerson, relationship: e.target.value})}
                          className="px-3 py-2 text-sm border border-blue-200 rounded-md focus:ring-1 focus:ring-blue-500">
                          <option value="">Relationship...</option>
                          <option value="son">Son</option>
                          <option value="daughter">Daughter</option>
                          <option value="spouse">Spouse</option>
                          <option value="caretaker">Caretaker</option>
                          <option value="facility_staff">Facility Staff</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input type="tel" value={contactPerson.phone} onChange={e => setContactPerson({...contactPerson, phone: e.target.value})}
                          placeholder="Phone number" className="px-3 py-2 text-sm border border-blue-200 rounded-md focus:ring-1 focus:ring-blue-500" />
                        <input type="email" value={contactPerson.email} onChange={e => setContactPerson({...contactPerson, email: e.target.value})}
                          placeholder="Email" className="px-3 py-2 text-sm border border-blue-200 rounded-md focus:ring-1 focus:ring-blue-500" />
                      </div>
                    </div>
                  )}

                  {/* Person receiving service */}
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-semibold text-gray-700">
                      {isForSelf === false ? 'Person Receiving Service' : 'Client Details'}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="text" value={newClient.first_name} onChange={e => {
                        setNewClient({...newClient, first_name: e.target.value});
                        searchPersonMatches(e.target.value, newClient.last_name);
                      }}
                        placeholder="First name *" className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800"
                        autoFocus={isForSelf !== false} />
                      <input type="text" value={newClient.last_name} onChange={e => {
                        setNewClient({...newClient, last_name: e.target.value});
                        searchPersonMatches(newClient.first_name, e.target.value);
                      }}
                        placeholder="Last name" className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800" />
                    </div>
                    {personMatches.length > 0 && !selectedClient && (
                      <div className="bg-amber-50 border border-amber-300 rounded-lg p-2">
                        <p className="text-[10px] font-semibold text-amber-700 mb-1">Existing person found — use instead?</p>
                        {personMatches.map(p => (
                          <button key={p.id} type="button" onClick={async () => {
                            setNewClient({ first_name: p.first_name || '', last_name: p.last_name || '', phone: p.phone_number || '', email: p.email || '' });
                            setPersonMatches([]);
                            // Try to find existing Client for this Person
                            try {
                              const clients = await apiService.get('/amn/clients/', { person: p.id, page_size: 1 });
                              const found = (clients.results || [])[0];
                              if (found) {
                                setSelectedClient(found);
                                setShowNewClient(false);
                                if (found.effective_address || found.person?.address) setAddress(found.effective_address || found.person?.address || '');
                                if (found.effective_city || found.person?.city) setCity(found.effective_city || found.person?.city || '');
                                if (found.effective_zip_code || found.person?.zip_code) setZipCode(found.effective_zip_code || found.person?.zip_code || '');
                              }
                            } catch {}
                          }}
                            className="w-full text-left px-2 py-1.5 text-sm hover:bg-amber-100 rounded flex items-center justify-between">
                            <span className="font-medium text-gray-900">{p.first_name} {p.last_name}</span>
                            <span className="text-xs text-gray-500">
                              {p.phone_number || 'no phone'}
                              {p.email ? ` · ${p.email}` : ''}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    <input type="tel" value={newClient.phone} onChange={e => setNewClient({...newClient, phone: e.target.value})}
                      placeholder={isForSelf === false ? "Their phone (optional)" : "Phone number"}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800" />
                    <input type="email" value={newClient.email} onChange={e => setNewClient({...newClient, email: e.target.value})}
                      placeholder="Email for receipts (optional)" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800" />
                    <div className="grid grid-cols-2 gap-2">
                      <input type="number" value={clientAge} onChange={e => setClientAge(e.target.value)}
                        placeholder="Age" className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800" />
                      <input type="text" value={medicalNotes} onChange={e => setMedicalNotes(e.target.value)}
                        placeholder="Diabetes, mobility..."
                        className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input type="text" value={clientSearch}
                    onChange={e => { setClientSearch(e.target.value); if (clientSearchTimeout.current) clearTimeout(clientSearchTimeout.current); clientSearchTimeout.current = setTimeout(() => searchClients(e.target.value), 300); }}
                    placeholder="Search by name or phone..." className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800" autoFocus />
                  {clientResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {clientResults.map(c => (
                        <button key={c.id} type="button" onClick={() => { setSelectedClient(c); setClientSearch(''); setClientResults([]); setAddress(c.effective_address || c.override_address || c.person?.address || ''); setCity(c.effective_city || c.override_city || c.person?.city || ''); setZipCode(c.effective_zip_code || c.override_zip_code || c.person?.zip_code || ''); if (c.last_booking_note) setNote(c.last_booking_note); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0">
                          <span className="font-medium">{c.person?.first_name} {c.person?.last_name}</span>
                          {c.person?.phone_number && <span className="text-gray-400 ml-2 text-xs">{c.person.phone_number}</span>}
                          <span className="text-gray-400 text-xs ml-2">({c.appointment_count || 0} appts)</span>
                          {c.has_card_on_file ? <span className="text-green-500 text-xs ml-1">{'💳'}</span> : <span className="text-amber-500 text-xs ml-1">{'⚠️'} no card</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Address */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="Street address"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800 mb-1" />
                <div className="grid grid-cols-2 gap-1">
                  <input type="text" value={city} onChange={e => setCity(e.target.value)} placeholder="City"
                    className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800" />
                  <input type="text" value={zipCode} onChange={e => setZipCode(e.target.value)} placeholder="Zip"
                    className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800" />
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Note</label>
                <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Gate code, parking, etc..."
                  rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800" />
              </div>

              <button onClick={async () => {
                setCardError(null);
                // For new clients: create Person + Client first
                if (!selectedClient && showNewClient && newClient.first_name.trim()) {
                  setCreatingClient(true);
                  try {
                    const resp = await apiService.post('/amn/clients/', {
                      first_name: newClient.first_name.trim(),
                      last_name: (newClient.last_name || '').trim(),
                      phone: newClient.phone || '',
                      email: newClient.email || '',
                      address: address || '',
                      city: city || '',
                      zip_code: zipCode || '',
                    });
                    setSelectedClient(resp);
                    setShowNewClient(false);
                  } catch (err) {
                    // If duplicate phone, try to find existing client
                    if (newClient.phone) {
                      try {
                        const search = await apiService.get('/amn/clients/', { search: newClient.phone, page_size: 1 });
                        const found = (search.results || [])[0];
                        if (found) { setSelectedClient(found); setShowNewClient(false); }
                        else { setCardError(err.message || 'Could not create client'); setCreatingClient(false); return; }
                      } catch { setCardError(err.message || 'Could not create client'); setCreatingClient(false); return; }
                    } else { setCardError(err.message || 'Could not create client'); setCreatingClient(false); return; }
                  } finally { setCreatingClient(false); }
                }
                // Load Stripe if not loaded
                if (!stripePromise) {
                  try {
                    const data = await apiService.getStripeKey();
                    setStripePromise(loadStripe(data.publishable_key));
                  } catch {}
                }
                setStep('card');
              }}
                disabled={creatingClient || (!selectedClient && !(showNewClient && newClient.first_name.trim()))}
                className="w-full py-2.5 bg-red-800 text-white text-sm font-medium rounded-lg hover:bg-red-900 disabled:opacity-50">
                {creatingClient ? 'Setting up...' : 'Continue to Payment'}
              </button>
              {cardError && <p className="text-xs text-red-600 mt-1">{cardError}</p>}
            </div>
          )}

          {/* Step: Payment Method — Inline Stripe Elements */}
          {step === 'card' && (
            <div className="space-y-4" style={{ minHeight: 350 }}>
              {!selectedClient?.has_card_on_file && (
                <p className="text-sm text-gray-600">
                  <em style={{color:'#8B2500'}}>"Last but not least, I just need to save a valid credit card on file for the appointment. Charge is only going to be processed after the service is completed."</em>
                </p>
              )}

              {cardError && (
                <div className="bg-red-50 border border-red-200 p-2 text-xs text-red-800 rounded">{cardError}</div>
              )}

              {selectedClient?.has_card_on_file ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-green-800">Card on file</p>
                  <p className="text-xs text-green-700 mt-0.5">{selectedClient.card_brand} ending in {selectedClient.card_last_four}</p>
                  <button onClick={() => setStep('confirm')}
                    className="mt-3 w-full py-2.5 bg-red-800 text-white text-sm font-medium rounded-lg hover:bg-red-900">
                    Review Booking
                  </button>
                </div>
              ) : cardCollected ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-green-800">Card saved successfully</p>
                  <button onClick={() => setStep('confirm')}
                    className="mt-3 w-full py-2.5 bg-red-800 text-white text-sm font-medium rounded-lg hover:bg-red-900">
                    Review Booking
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Inline Stripe Elements card form */}
                  {selectedClient && stripePromise ? (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs font-medium text-gray-700 mb-2">Enter card details</p>
                      <Elements stripe={stripePromise}>
                        <InlineCardForm
                          clientId={selectedClient.id}
                          apiService={apiService}
                          onSuccess={(result) => {
                            setCardCollected(true);
                            setSelectedClient(prev => ({
                              ...prev,
                              has_card_on_file: true,
                              card_brand: result.card_brand,
                              card_last_four: result.card_last_four,
                            }));
                            setCardError(null);
                          }}
                          onError={(msg) => setCardError(msg)}
                        />
                      </Elements>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
                      <p className="text-xs text-gray-500 mt-1">Loading payment form...</p>
                    </div>
                  )}

                  {/* Send link option */}
                  {selectedClient && (
                    <button onClick={() => {
                      handleSendCardLink();
                      setTimeout(() => setStep('confirm'), 1500);
                    }} disabled={cardLinkSending || cardLinkSent}
                      className="w-full p-3 rounded-lg border border-blue-200 hover:bg-blue-50 transition-colors flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <div className="text-left">
                        <p className="text-sm font-medium text-blue-800">
                          {cardLinkSending ? 'Sending...' : cardLinkSent ? 'Link Sent ✓' : 'Or send card link via SMS'}
                        </p>
                      </div>
                    </button>
                  )}

                  <button onClick={() => setStep('confirm')}
                    className="w-full py-2 text-gray-500 text-xs font-medium hover:text-gray-700 transition-colors">
                    Skip — book without card
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Services Selection */}
          {step === 'services' && (() => {
            const addOnKeywords = ['add-on', 'other add-on', 'travel fee', 'cancellation', 'gratuity', 'tip', 'gel polish l', 'restorative foot'];
            const isAddOn = (name) => addOnKeywords.some(k => name.toLowerCase().includes(k));
            const filtered = serviceSearch.length > 0
              ? allServices.filter(s => s.name.toLowerCase().includes(serviceSearch.toLowerCase()))
              : allServices;
            const alpha = (a, b) => a.name.localeCompare(b.name);
            const notSelected = filtered.filter(s => !selectedServiceIds.includes(s.id));
            const selected = filtered.filter(s => selectedServiceIds.includes(s.id)).sort(alpha);
            const homeServices = notSelected.filter(s => !isAddOn(s.name) && !s.is_alf).sort(alpha);
            const alfServices = notSelected.filter(s => !isAddOn(s.name) && s.is_alf).sort(alpha);
            const isAlfAddOn = (s) => isAddOn(s.name) && (s.is_alf || s.name.toLowerCase().startsWith('alf'));
            const homeAddOns = notSelected.filter(s => isAddOn(s.name) && !isAlfAddOn(s)).sort(alpha);
            const alfAddOns = notSelected.filter(s => isAlfAddOn(s)).sort(alpha);

            const renderService = (svc) => {
              const isSelected = selectedServiceIds.includes(svc.id);
              const qty = serviceQuantities[svc.id] || 1;
              return (
                <div key={svc.id} className={`flex items-center justify-between px-3 py-2 text-sm border-b border-gray-100 last:border-0 ${isSelected ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                  <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                    <input type="checkbox" checked={isSelected}
                      onChange={() => {
                        setSelectedServiceIds(prev => prev.includes(svc.id) ? prev.filter(id => id !== svc.id) : [...prev, svc.id]);
                        if (!isSelected) setServiceQuantities(prev => ({ ...prev, [svc.id]: 1 }));
                      }}
                      className="w-3.5 h-3.5 text-red-800 rounded flex-shrink-0" />
                    <span className={`truncate ${isSelected ? 'font-medium' : ''}`}>{svc.name}</span>
                    {svc.duration_minutes && <span className="text-[10px] text-gray-400 flex-shrink-0">{svc.duration_minutes}min</span>}
                  </label>
                  {isSelected && (
                    <div className="flex items-center gap-1.5 mr-2 flex-shrink-0">
                      <button type="button" onClick={() => setServiceQuantities(prev => ({ ...prev, [svc.id]: Math.max(1, (prev[svc.id] || 1) - 1) }))}
                        className="w-5 h-5 rounded bg-gray-200 text-gray-700 text-xs flex items-center justify-center hover:bg-gray-300">−</button>
                      <span className="text-xs font-semibold w-4 text-center">{qty}</span>
                      <button type="button" onClick={() => setServiceQuantities(prev => ({ ...prev, [svc.id]: (prev[svc.id] || 1) + 1 }))}
                        className="w-5 h-5 rounded bg-gray-200 text-gray-700 text-xs flex items-center justify-center hover:bg-gray-300">+</button>
                    </div>
                  )}
                  <span className="text-xs text-gray-500 flex-shrink-0">{svc.price ? `$${(parseFloat(svc.price) * (isSelected ? qty : 1)).toFixed(2)}` : '—'}</span>
                </div>
              );
            };

            return (
            <div className="space-y-3">
              <label className="block text-xs font-medium text-gray-700">
                Select Services {selectedServiceIds.length > 0 && (
                  <span className="text-green-700 font-normal">
                    ({selectedServiceIds.length} selected - ${allServices.filter(s => selectedServiceIds.includes(s.id)).reduce((sum, s) => sum + parseFloat(s.price || 0) * (serviceQuantities[s.id] || 1), 0).toFixed(2)})
                  </span>
                )}
              </label>
              <input
                type="text"
                value={serviceSearch}
                onChange={(e) => setServiceSearch(e.target.value)}
                placeholder="Type to search services..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-red-800 focus:border-red-800"
                autoFocus
              />

              {/* Selected services */}
              {selected.length > 0 && (
                <div className="border border-green-200 rounded-md bg-green-50/30">
                  <div className="px-3 py-1.5 bg-green-100/50 border-b border-green-200">
                    <span className="text-[10px] font-semibold text-green-700 uppercase tracking-wide">Selected</span>
                  </div>
                  {selected.map(renderService)}
                </div>
              )}

              {/* Main services */}
              {homeServices.length > 0 && (
                <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto">
                  <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 sticky top-0">
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Main Services</span>
                  </div>
                  {homeServices.map(renderService)}
                </div>
              )}

              {/* Add-ons */}
              {homeAddOns.length > 0 && (
                <div className="border border-gray-200 rounded-md max-h-36 overflow-y-auto">
                  <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 sticky top-0">
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Add-ons & Fees</span>
                  </div>
                  {homeAddOns.map(renderService)}
                </div>
              )}

              {/* ALF services */}
              {alfServices.length > 0 && (
                <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto">
                  <div className="px-3 py-1.5 bg-purple-50 border-b border-purple-200 sticky top-0">
                    <span className="text-[10px] font-semibold text-purple-600 uppercase tracking-wide">ALF Services</span>
                  </div>
                  {alfServices.map(renderService)}
                </div>
              )}

              {/* ALF Add-ons */}
              {alfAddOns.length > 0 && (
                <div className="border border-gray-200 rounded-md max-h-36 overflow-y-auto">
                  <div className="px-3 py-1.5 bg-purple-50 border-b border-purple-200 sticky top-0">
                    <span className="text-[10px] font-semibold text-purple-600 uppercase tracking-wide">ALF Add-ons</span>
                  </div>
                  {alfAddOns.map(renderService)}
                </div>
              )}

              {filtered.length === 0 && serviceSearch && (
                <p className="text-xs text-gray-400 text-center py-3">No services matching "{serviceSearch}"</p>
              )}

              <button onClick={() => { setStep('calendar'); setWeekOffset(0); loadCalendarData(selectedTech.id, 0); }}
                disabled={selectedServiceIds.length === 0}
                className="w-full py-2.5 bg-red-800 text-white text-sm font-medium rounded-lg hover:bg-red-900 disabled:opacity-50">
                Choose Date & Time
              </button>
            </div>
            );
          })()}

          {/* Step 6: Confirm & Book */}
          {step === 'confirm' && (
            <div className="space-y-3">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-xs">{error}</div>}

              <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Client</span><span className="font-medium">{selectedClient ? (selectedClient.person_name || `${selectedClient.person?.first_name || ''} ${selectedClient.person?.last_name || ''}`.trim() || 'Unknown') : `${newClient.first_name} ${newClient.last_name} (new)`}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Technician</span><span className="font-medium">{selectedTech?.name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Date & Time</span><span className="font-medium">{new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {selectedTime}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Services</span><span className="font-medium">{allServices.filter(s => selectedServiceIds.includes(s.id)).map(s => s.name).join(', ')}</span></div>
                <div className="flex justify-between border-t border-gray-200 pt-2"><span className="text-gray-500 font-medium">Total</span><span className="font-bold text-gray-900">${allServices.filter(s => selectedServiceIds.includes(s.id)).reduce((sum, s) => sum + parseFloat(s.price || 0), 0).toFixed(2)}</span></div>
                {address && <div className="flex justify-between"><span className="text-gray-500">Address</span><span className="font-medium">{address}{city ? `, ${city}` : ''}{zipCode ? ` ${zipCode}` : ''}</span></div>}
                {clientAge && <div className="flex justify-between"><span className="text-gray-500">Age</span><span className="font-medium">{clientAge}</span></div>}
                {medicalNotes && <div className="flex justify-between"><span className="text-gray-500">Medical</span><span className="font-medium text-amber-700">{medicalNotes}</span></div>}
                {isForSelf === false && contactPerson.name && (
                  <div className="flex justify-between border-t border-gray-200 pt-2">
                    <span className="text-gray-500">Main Contact</span>
                    <span className="font-medium">{contactPerson.name}{contactPerson.relationship ? ` (${contactPerson.relationship})` : ''}{contactPerson.phone ? ` · ${contactPerson.phone}` : ''}</span>
                  </div>
                )}
                {selectedClient?.has_card_on_file ? (
                  <div className="flex items-center gap-1 pt-1">
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">💳 {selectedClient.card_brand} ****{selectedClient.card_last_four}</span>
                  </div>
                ) : cardCollected ? (
                  <div className="flex items-center gap-1 pt-1">
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">💳 Card saved</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 pt-1">
                    <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">No card — payment after service</span>
                  </div>
                )}
              </div>

              <button onClick={handleBook} disabled={submitting}
                className="w-full py-2.5 bg-red-800 text-white text-sm font-medium rounded-lg hover:bg-red-900 disabled:opacity-50">
                {submitting ? 'Creating...' : 'Confirm & Book'}
              </button>
            </div>
          )}

          {/* Step 7: Success */}
          {step === 'success' && (
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <UserCheck className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-sm font-medium text-gray-900 mb-1">Appointment Booked!</p>
              <p className="text-xs text-gray-500 mb-4">{new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at {selectedTime} with {selectedTech?.name}</p>
              <div className="flex flex-col gap-2">
                {bookedAppointmentId && (
                  <button onClick={() => navigate(`/appointments/${bookedAppointmentId}`)}
                    className="px-4 py-2.5 text-sm font-medium text-white bg-red-800 rounded-md hover:bg-red-900">
                    View Appointment
                  </button>
                )}
                {selectedClient && !selectedClient.has_card_on_file && !cardCollected && (
                  <button onClick={() => navigate(`/clients/${selectedClient.id}`)}
                    className="px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100">
                    Add Card on Client Page
                  </button>
                )}
                <button onClick={handleClose}
                  className="px-4 py-2 text-xs text-gray-500 hover:text-gray-700">
                  Book Another
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

export default BookingAssistant;
